import * as THREE from 'three';
import CameraControls from 'camera-controls';
import Player from 'video.js/dist/types/player';
import { IVideoTextureEngine } from './engines/IVideoTextureEngine';
import { VideoEngineFactory } from './engines/VideoEngineFactory';
import type { SpatialAudioFacade } from './core/PlaybackCore';
import { paneRects, SINGLE_PANE, DEFAULT_FOUR_VIEW, type PaneLayout, type FourViewFraming } from './splitLayout';

// Install CameraControls
CameraControls.install({ THREE });

// Zoom configuration interface
interface ZoomConfig {
  // Manual pinch zoom speed (gyro mode)
  manualZoomSpeed: number;

  // Wheel zoom. A mouse wheel is a mouse wheel on every machine, so this does
  // NOT vary by device — see the note on the values below.
  wheelDollySpeed: number;

  // Two-finger pinch zoom. Continuous, and genuinely device-dependent.
  pinchDollySpeed: number;

  // Transition thresholds
  normalToTopThreshold: number;
  topToNormalThreshold: number;
  bottomUpToNormalThreshold: number;
}

// Camera preset configurations
interface ViewPreset {
  x: number;
  y: number;
  z: number;
  targetX: number;
  targetY: number;
  targetZ: number;
  zoomFactor: number;
  minPolarAngle: number;
  maxPolarAngle: number;
  minZoom: number;
  maxZoom: number;
  compassOffset: number;
  // Initial manual yaw rotation offset in degrees (0 = North, 90 = East, 180 = South, 270 = West)
  // In gyroscope mode, this becomes the initial offset that users can adjust via finger dragging
  // This allows the view to start at the desired orientation relative to the device's physical position
  initialCameraRotation: number;
}

type ViewPresetKey = 'default' | 'topDown' | 'bottomUp';
type ViewPresets = Record<ViewPresetKey, ViewPreset>;

/**
 * A number from the query string, or null when absent or out of range.
 *
 * Zoom feel is found by scrolling, not by reasoning about it, so the two dials
 * that decide it are exposed the way `?debug=true` and `?radar=` already are:
 * live, per-URL, no rebuild. Nothing reads these unless a URL asks, so ordinary
 * traffic always gets the tuned defaults below.
 */
const numberParam = (params: URLSearchParams, key: string, min: number, max: number): number | null => {
  const raw = params.get(key);
  if (raw === null || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < min || n > max) return null;
  return n;
};

const getZoomConfig = (isMobile: boolean): ZoomConfig => {
  return {
    manualZoomSpeed: isMobile ? 1.5 : 1,
    // ONE NUMBER USED TO DO BOTH OF THESE, and that was the bug.
    //
    // camera-controls scales zoom by 0.95^(delta * dollySpeed) and applies the
    // same `dollySpeed` to the wheel AND to a two-finger pinch. Those are not
    // the same gesture: a wheel arrives as discrete notches (deltaY 100, which
    // becomes delta 3.33), a pinch as a continuous stream of small deltas. A
    // pinch needs a big multiplier; a wheel does not.
    //
    // So a device classified as a phone drove the WHEEL at 10.0, which is
    // 0.95^33 — a 82% zoom change per notch. One notch pinned the zoom to its
    // limit and tripped the flip to top-down. And "is a phone" is decided by
    // touch support AND a window <= 768px wide, which a touchscreen laptop, a
    // snapped window, or simply browser zoom above 100% all satisfy. A desktop
    // with a mouse got phone pinch tuning on its wheel.
    //
    // The wheel value is therefore FIXED ACROSS DEVICES. 0.8 gives 0.95^2.67 —
    // about 12.8% per notch, and a measured 6 notches from the default zoom to
    // the top-down flip on every device classification. It was arrived at by
    // scrolling until it felt right, not by arithmetic. `?dolly=<n>` overrides
    // it live for doing that again.
    //
    // Note the runway is the real constraint: normal view only spans zoom 0.9
    // down to the 0.45 flip threshold, so this dial trades "flips too easily"
    // against "slow to get back". Widening that runway is a separate change.
    wheelDollySpeed: 0.8,

    // Pinch keeps the per-device tuning it always had: that is how fast a
    // gesture moves the zoom, not how far it may go.
    pinchDollySpeed: isMobile ? 10.0 : 1,

    // The automatic view switches, the same on every device (Kai, 28 Sep),
    // each at whichever of the old phone / desktop values leaves the viewer
    // more room before the view changes under them.
    normalToTopThreshold: 0.45,       // was 0.45 / 0.5 — zoom further out before Above
    topToNormalThreshold: 1.2,
    bottomUpToNormalThreshold: 1.0,   // was 1.0 / 0.7 — zoom further in before Normal
  }
}

// ONE set of views for every device (Kai, 28 Sep). There used to be a phone
// set and a desktop set, differing in every number with no recorded reason —
// and that difference is what made Chrome on a Mac zoom out further than
// Safari, the day Chrome started being misread as a phone. Where the two sets
// differed, the value that constrains the viewer LESS was kept: "the user
// should be able to do what they want without constriction".
//
// A phone held upright still sees less ACROSS than a desktop at the same zoom:
// zoom sets the up-and-down angle, and the screen's shape sets the across
// (0.2 is ~165° across on a wide window, ~121° on a portrait phone). That is
// the screen, not a setting, and is left alone.
//
// The limits only mean anything together with the automatic view switches in
// getZoomConfig: in Normal, zooming out switches to Above before minZoom is
// reached, and in Below, zooming in switches to Normal before maxZoom is.
const getViewPresets = (): ViewPresets => {

  const basePreset: ViewPreset = {
    x: 0,
    y: 0,
    z: 0,
    targetX: 0,
    targetY: 0,
    targetZ: 0,
    zoomFactor: 0,
    minPolarAngle: 0,
    maxPolarAngle: 0,
    minZoom: 0,
    maxZoom: 0,
    compassOffset: 0,
    initialCameraRotation: 0,
  }

  return {
    default: {
      ...basePreset,
      z: 0.1,
      // Where each view STARTS is not a limit, so not part of the "less
      // constriction" rule. 1, the desktop value: 0.9 tipped the start-up
      // texture probe into a false "360 is broken" on a dark opening frame,
      // see probeTexture.
      zoomFactor: 1,     // was phone 0.9 / desktop 1
      minPolarAngle: 45,
      maxPolarAngle: 135,
      minZoom: 0.2,      // was 0.2 / 0.4
      maxZoom: 3.5,      // was 3.5 / 2.5
    },
    topDown: {
      ...basePreset,
      y: 500,
      z: 0.01,
      zoomFactor: 0.7,   // was 1 / 0.7
      minPolarAngle: 0,
      maxPolarAngle: 45,
      minZoom: 0.15,     // was 0.3 / 0.15
      maxZoom: 2.2,      // was 2.2 / 1.6
    },
    bottomUp: {
      ...basePreset,
      y: -500,
      z: 0.01,
      zoomFactor: 0.5,   // was 0.5 / 0.55
      minPolarAngle: 135,
      maxPolarAngle: 180,
      // ~165° across on a wide window. Gavin called it "great" (28 Sep).
      minZoom: 0.2,      // was 0.2 / 0.4
      maxZoom: 1.8,      // was 1.8 / 1.3
    },
  }
}

/**
 * Everything that decides how wide the view is, for the ?zoominfo=1 readout.
 * Built for one question (Gavin, 28 Sep): zoomed all the way out, Chrome and
 * Brave on a Mac show far more of the room than Safari and Firefox do. By the
 * code they should not differ — the zoom stops at the same preset limit in
 * every browser — so the readout shows which of these numbers actually does.
 */
export interface ZoomDiagnostics {
  preset: 'default' | 'topDown' | 'bottomUp';
  zoom: number;
  minZoom: number;
  maxZoom: number;
  // The lowest zoom (widest view) reached since the page loaded, and in which
  // preset — so one screenshot after zooming out says how far it got.
  widest: { zoom: number; preset: 'default' | 'topDown' | 'bottomUp'; fovAcross: number } | null;
  fovAcross: number;
  fovUpDown: number;
  lensFov: number;
  aspect: number;
  containerW: number;
  containerH: number;
  canvasW: number;
  canvasH: number;
  pixelRatio: number;
  devicePixelRatio: number;
  // True when the phone/tablet pinch speed and motion control were chosen.
  // The views' zoom limits no longer depend on it — see getViewPresets.
  phoneTuning: boolean;
  wheelEvents: number;
  lastWheel: { deltaY: number; deltaMode: number; ctrlKey: boolean } | null;
  // Safari's own trackpad-pinch events, which camera-controls never sees.
  gestureEvents: number;
  // The raw inputs to the phone/desktop decision, so a wrong call shows why.
  requestPermissionApi: boolean;
  maxTouchPoints: number;
}

// Presentation-only callbacks. Everything media-related (play state, time,
// duration, volume, buffering, errors, quality levels) is reported by
// PlaybackCore, which owns the pipeline this controller renders from.
export interface VideoPlayer360Callbacks {
  /** `fov` and `fovVertical` are the angles actually on screen, across and
   *  up-and-down — see effectiveFov(), not the camera's fixed lens angle. */
  onCameraUpdate: (yaw: number, pitch: number, fov: number, fovVertical: number) => void;
  onViewPresetChange: (preset: 'default' | 'topDown' | 'bottomUp') => void;
  onGyroStateChange: (enabled: boolean) => void;
  onFullscreenChange?: (isFullscreen: boolean) => void;
  onUserCameraInteraction?: () => void;
  // Split screen: the same two reports for the right-hand camera.
  onSplitCameraUpdate?: (yaw: number, pitch: number, fov: number, fovVertical: number) => void;
  onSplitViewPresetChange?: (preset: 'default' | 'topDown' | 'bottomUp') => void;
}

type PresetName = 'default' | 'topDown' | 'bottomUp';

// 0 is the main camera — the only one outside split screen, the first pane
// in it. 1–3 are the other panes' cameras, in reading order (see
// splitLayout.ts), which exist only while the layout has that many panes.
export type PaneIndex = 0 | 1 | 2 | 3;

// Everything that belongs to ONE camera: the lens, its input handling, which
// view preset it is in and where its viewer means to look. Split screen runs
// two of these over the one scene.
interface CameraRig {
  camera: THREE.PerspectiveCamera;
  controls: CameraControls;
  preset: PresetName;
  lastTransitionTime: number;
  isTransitioning: boolean;
  // Guard to distinguish programmatic rotations from user drags
  isProgrammaticRotation: boolean;
  // Where the user intends to look (world space); persists across view modes
  intendedOrientation: THREE.Quaternion;
}

export class VideoPlayer360Controller {
  // Three.js objects
  private scene: THREE.Scene;
  private renderer: THREE.WebGLRenderer;
  private mesh: THREE.Mesh;
  private videoEngine: IVideoTextureEngine;
  private clock: THREE.Clock;

  // iOS-specific state
  private isIOS: boolean = false;

  // video.js player, owned by PlaybackCore — injected here, never disposed here.
  readonly player: Player;

  // Container refs
  private container: HTMLElement;
  private videoElement: HTMLVideoElement;

  // Callbacks for React UI updates
  private callbacks: VideoPlayer360Callbacks;

  // Animation state
  private animationFrameId: number | null = null;

  // The main camera. The accessors below keep the names the rest of this
  // file has always used for it — gyro, fullscreen and the diagnostics only
  // ever concern the main camera. Code that has to work on either camera takes
  // a CameraRig instead.
  private main = {
    preset: 'default',
    lastTransitionTime: 0,
    isTransitioning: false,
    isProgrammaticRotation: false,
  } as CameraRig;
  private get camera(): THREE.PerspectiveCamera { return this.main.camera; }
  private set camera(camera: THREE.PerspectiveCamera) { this.main.camera = camera; }
  private get controls(): CameraControls { return this.main.controls; }
  private set controls(controls: CameraControls) { this.main.controls = controls; }
  private get currentPreset(): PresetName { return this.main.preset; }
  private get intendedOrientation(): THREE.Quaternion { return this.main.intendedOrientation; }
  private set intendedOrientation(q: THREE.Quaternion) { this.main.intendedOrientation = q; }

  // Debounced logging state
  private debug: boolean = false;

  // ?zoomsmooth=<seconds>, or null for the tuned default.
  private smoothTimeOverride: number | null = null;

  // Arrow properties, so each is a stable reference that removeEventListener
  // can actually find on teardown.
  private armWheelZoom = (event: WheelEvent) => {
    this.controls.dollySpeed = this.zoomConfig.wheelDollySpeed;
    for (const rig of this.extraRigs) rig.controls.dollySpeed = this.zoomConfig.wheelDollySpeed;
    this.wheelEvents++;
    this.lastWheel = { deltaY: event.deltaY, deltaMode: event.deltaMode, ctrlKey: event.ctrlKey };
  };
  // ?zoominfo=1 bookkeeping — see ZoomDiagnostics. Cheap enough to keep
  // unconditionally: two counters, one small object, one comparison a frame.
  private wheelEvents = 0;
  private lastWheel: ZoomDiagnostics['lastWheel'] = null;
  private gestureEvents = 0;
  private widest: ZoomDiagnostics['widest'] = null;
  private countGesture = () => { this.gestureEvents++; };
  // Anything that is not a mouse is a finger or a pen, and both of those zoom
  // by pinching. Deliberately on `pointerdown`, the SAME event stream
  // camera-controls itself listens to, rather than `touchstart`: a browser
  // that emits pointer events without touch events would otherwise leave the
  // wheel speed armed for a pinch.
  private armPinchZoom = (event: PointerEvent) => {
    if (event.pointerType === 'mouse') return;
    this.controls.dollySpeed = this.zoomConfig.pinchDollySpeed;
    for (const rig of this.extraRigs) rig.controls.dollySpeed = this.zoomConfig.pinchDollySpeed;
  };
  private lastLogTime = 0;
  private lastLoggedZoom = 0;
  private lastLoggedYaw = 0;
  private lastLoggedPosition = { x: 0, y: 0, z: 0 };
  private lastZoomTransitionLogTime = 0;
  private lastLoggedPolarAngle = 0;
  private logPolarChangesUntil = 0; // Timestamp - log polar changes frequently until this time

  // Transcript view offset (shifts rendered view when transcript panel is open)
  private containerResizeObserver: ResizeObserver | null = null;

  // Device detection and zoom configuration
  private hasGyroscopeDevice = false;
  private zoomConfig: ZoomConfig;
  private viewPresets: Record<'default' | 'topDown' | 'bottomUp', ViewPreset>;

  // Gyroscope state
  private isGyroActive = false;
  private gyroReferenceQuat: THREE.Quaternion | null = null;
  private currentDeviceQuat = new THREE.Quaternion();
  private screenOrientation = 0;
  private deviceOrientationHandler: ((event: DeviceOrientationEvent) => void) | null = null;

  // Raw device orientation data for modulation functions
  private rawAlpha = 0;  // Compass heading (0-360°)
  private rawBeta = 0;   // Front-back tilt (-180 to 180°)
  private rawGamma = 0;  // Left-right tilt (-90 to 90°)

  // Reference device orientation (captured at gyro activation)
  private rawAlphaReference = 0;
  private rawBetaReference = 0;
  private rawGammaReference = 0;

  // Industry-standard DeviceOrientationControls quaternion state
  private deviceQuat = new THREE.Quaternion();           // Absolute device orientation
  private gyroOffsetQuat = new THREE.Quaternion();      // Offset from reference
  private smoothedOffsetQuat = new THREE.Quaternion();  // Smoothed offset (anti-jitter)

  // Device-to-camera transformation constants
  private q1 = new THREE.Quaternion(-Math.sqrt(0.5), 0, 0, Math.sqrt(0.5));
  private zee = new THREE.Vector3(0, 0, 1);

  // Gyroscope smoothing configuration
  private gyroSmoothingFactor = 0.2; // 0.1 (smooth) to 0.5 (responsive)

  // Spatial audio facade. The manager itself lives in PlaybackCore because
  // createMediaElementSource can only ever be called once per element, and the
  // element outlives every sphere mount.
  private spatialAudio: SpatialAudioFacade;

  // Rotation adjustment
  private rotationAdjustment = 0;

  // Fake fullscreen state (CSS-based fallback for iOS)
  private isFakeFullscreen = false;
  private playerWrapper: HTMLElement | null = null;
  private originalStyles: { [key: string]: string } = {};

  private onContextLost?: () => void;

  // Split screen. More cameras over the SAME scene, each drawn into its own
  // pane of the one canvas — one video element, one texture upload, so every
  // pane shows the same decoded frame and none can drift. In the two-pane
  // layout the right pane is a transparent element over that half which owns
  // its pointer input, since camera-controls binds one camera to one element.
  // In a locked layout (all views) no camera takes input at all.
  private layout: PaneLayout = SINGLE_PANE;
  // The locked panes' panel shape and zoom (splitLayout.ts), and the
  // headings they were last aimed at, to re-aim when it changes.
  private fourView: FourViewFraming = DEFAULT_FOUR_VIEW;
  private lockedHeadings: number[] = [];
  private extraRigs: CameraRig[] = [];
  private splitPane: HTMLDivElement | null = null;
  private rendererSize = new THREE.Vector2();

  // The media pipeline (element, player, spatial audio) is created and owned by
  // PlaybackCore and injected here: this controller only renders it.
  constructor(
    container: HTMLElement,
    videoElement: HTMLVideoElement,
    player: Player,
    spatialAudio: SpatialAudioFacade,
    callbacks: VideoPlayer360Callbacks
  ) {
    this.container = container;
    this.videoElement = videoElement;
    this.player = player;
    this.spatialAudio = spatialAudio;
    this.callbacks = callbacks;

    // Check for debug query parameter
    const urlParams = new URLSearchParams(window.location.search);
    this.debug = urlParams.get('debug') === 'true';

    // Detect device type based on gyroscope availability
    this.hasGyroscopeDevice = VideoPlayer360Controller.hasGyroscope();
    this.zoomConfig = getZoomConfig(this.hasGyroscopeDevice)

    // ?dolly=<n> how far one wheel notch moves the zoom; ?zoomsmooth=<n> how
    // long it takes to get there, in seconds. Both for finding the feel.
    const dollyOverride = numberParam(urlParams, 'dolly', 0.05, 20);
    if (dollyOverride !== null) this.zoomConfig = { ...this.zoomConfig, wheelDollySpeed: dollyOverride };
    this.smoothTimeOverride = numberParam(urlParams, 'zoomsmooth', 0.01, 1);
    this.viewPresets = getViewPresets()

    // Initialize intended orientation from default preset
    // This represents where the user wants to look (world space)
    // Camera starts at 0° (North) - video sphere is rotated to align content
    const initialYaw = THREE.MathUtils.degToRad(this.viewPresets.default.initialCameraRotation);
    const initialEuler = new THREE.Euler(0, initialYaw, 0, 'YXZ');
    this.intendedOrientation = new THREE.Quaternion().setFromEuler(initialEuler);

    if (this.debug) {
      console.log('[Device Detection]', {
        hasGyroscope: this.hasGyroscopeDevice,
        zoomConfig: this.zoomConfig,
        viewPresets: this.hasGyroscopeDevice ? 'MOBILE' : 'DESKTOP',
        initialCameraRotation: this.viewPresets.default.initialCameraRotation,
        intendedYaw: initialYaw.toFixed(2) + ' rad (' + this.viewPresets.default.initialCameraRotation + '°)',
      });
    }

    // Initialize Three.js
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(
      75,
      container.clientWidth / container.clientHeight,
      0.1,
      1000
    );
    this.camera.position.z = 0.1;

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.xr.enabled = false;
    container.appendChild(this.renderer.domElement);

    this.clock = new THREE.Clock();

    // Detect iOS device
    this.isIOS = VideoPlayer360Controller.isIOSDevice();

    if (this.debug) {
      console.log('[iOS Detection]', {
        isIOS: this.isIOS,
        userAgent: navigator.userAgent.substring(0, 100),
      });
    }

    // Create 360 sphere
    const geometry = new THREE.SphereGeometry(500, 60, 40);
    geometry.scale(-1, 1, 1); // Invert for inside view

    // Initialize Video Engine using the Factory
    // This automatically selects the best strategy (Native, Sync, or Canvas)
    // based on the device capabilities.
    this.videoEngine = VideoEngineFactory.createEngine(videoElement);
    this.videoEngine.start();

    this.renderer.domElement.addEventListener('webglcontextlost', () => {
      this.onContextLost?.();
    });

    const material = new THREE.MeshBasicMaterial({ map: this.videoEngine.texture });
    this.mesh = new THREE.Mesh(geometry, material);
    // Rotate sphere -90° (270°) to align presenter/intro content to 0° (North)
    // This eliminates the need for camera rotation offsets
    this.mesh.rotation.y = -Math.PI / 2;
    this.scene.add(this.mesh);

    // Initialize camera controls
    this.controls = new CameraControls(this.camera, this.renderer.domElement);
    this.controls.azimuthRotateSpeed = -0.2;
    this.controls.polarRotateSpeed = -0.2;
    this.controls.minDistance = Number.EPSILON;
    this.controls.maxDistance = Infinity;
    this.controls.minZoom = 0.5;
    this.controls.maxZoom = 1.5;
    this.controls.dampingFactor = 0.02; // Fast, snappy response for view transitions
    this.controls.draggingDampingFactor = 0.1;
    // camera-controls has a single `dollySpeed` for wheel and pinch alike, so
    // the right one is armed as each gesture starts. These listeners are on the
    // CONTAINER in the capture phase, which is what guarantees they run before
    // camera-controls' own handlers on the canvas inside it — registration
    // order would not, since both would be listening on the same element.
    // Zoom itself is computed on the MOVE that follows, by which time the
    // correct speed has been armed by the down/wheel event.
    this.controls.dollySpeed = this.zoomConfig.wheelDollySpeed;
    this.container.addEventListener('wheel', this.armWheelZoom, { capture: true, passive: true });
    this.container.addEventListener('pointerdown', this.armPinchZoom, { capture: true, passive: true });
    this.container.addEventListener('gesturechange', this.countGesture, { capture: true, passive: true });
    this.controls.mouseButtons.wheel = CameraControls.ACTION.ZOOM;
    this.controls.touches.two = CameraControls.ACTION.TOUCH_ZOOM_TRUCK;
    this.controls.smoothTime = this.smoothTimeOverride ?? 0.08; // Faster preset transitions
    this.controls.draggingSmoothTime = 0.06;

    this.controls.addEventListener('controlstart', () => {
      if (!this.main.isProgrammaticRotation) {
        this.callbacks.onUserCameraInteraction?.();
      }
    });

    // Set initial view
    this.setViewPreset('default', false);

    // Start animation loop
    this.startAnimation();

    // Setup resize handlers. The ResizeObserver catches container-only size
    // changes (e.g. the transcript panel opening takes layout space) that
    // never fire a window resize.
    window.addEventListener('resize', this.handleResize);
    this.containerResizeObserver = new ResizeObserver(this.handleResize);
    this.containerResizeObserver.observe(this.container);

    // Setup orientation change handler
    window.addEventListener('orientationchange', this.handleOrientationChange);

    // Auto-enable gyroscope if available (skip on iOS - requires user gesture for permission)
    if (this.hasGyroscopeDevice && !VideoPlayer360Controller.needsPermissionRequest()) {
      this.enableGyro().catch((error) => {
        if (this.debug) console.log('[Gyro] Auto-enable failed:', error);
      });
    }
  }

  // Public API methods. Transport (play/pause/seek/volume/mute/quality) lives
  // on PlaybackCore — callers drive playback there, not through the surface.

  rotateTo(targetYawDegrees: number, animated = true, pane: PaneIndex = 0): void {
    const rig = this.rigFor(pane);
    if (!rig) return;
    rig.isProgrammaticRotation = true;
    rig.controls.rotateAzimuthTo(this.shortestAzimuthTo(rig, targetYawDegrees), animated);
    // Clear flag after controlstart event has fired
    setTimeout(() => { rig.isProgrammaticRotation = false; }, 0);
  }

  private rigFor(pane: PaneIndex): CameraRig | null {
    return pane === 0 ? this.main : this.extraRigs[pane - 1] ?? null;
  }

  // Point the camera at an absolute heading, e.g. restoring the camera a
  // viewer shared through the URL. Pitch is the view elevation reported by
  // onCameraUpdate (negative looks down), which is the polar angle less 90°;
  // it is clamped to the polar limits of the active view preset.
  setCamera(yawDegrees: number, pitchDegrees: number): void {
    this.main.isProgrammaticRotation = true;
    const polar = THREE.MathUtils.clamp(
      THREE.MathUtils.degToRad(pitchDegrees) + Math.PI / 2,
      this.controls.minPolarAngle,
      this.controls.maxPolarAngle
    );
    this.controls.rotateTo(this.shortestAzimuthTo(this.main, yawDegrees), polar, false);
    setTimeout(() => { this.main.isProgrammaticRotation = false; }, 0);
  }

  // CameraControls' azimuthAngle is accumulative (wraps beyond 2*PI), so an
  // absolute heading has to be expressed as the shortest delta from where the
  // camera currently sits.
  private shortestAzimuthTo(rig: CameraRig, targetYawDegrees: number): number {
    const targetAzimuth = THREE.MathUtils.degToRad(targetYawDegrees);
    const current = rig.controls.azimuthAngle;
    const currentNorm = ((current % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    let diff = targetAzimuth - currentNorm;
    if (diff > Math.PI) diff -= 2 * Math.PI;
    if (diff < -Math.PI) diff += 2 * Math.PI;
    return current + diff;
  }

  setActiveSpeaker(positionDegrees: number | null): void {
    this.spatialAudio.setActiveSpeaker(positionDegrees);
  }

  setSpatialAudioEnabled(enabled: boolean): void {
    this.spatialAudio.setEnabled(enabled);
  }

  isSpatialAudioEnabled(): boolean {
    return this.spatialAudio.isEnabled();
  }

  /**
   * Set the player wrapper element (parent of container) for fake fullscreen
   */
  setPlayerWrapper(wrapper: HTMLElement): void {
    this.playerWrapper = wrapper;
  }

  /**
   * Check if currently in any fullscreen mode (real or fake)
   */
  isInFullscreen(): boolean {
    const doc = document as Document & { webkitFullscreenElement?: Element };
    return !!(document.fullscreenElement || doc.webkitFullscreenElement || this.isFakeFullscreen);
  }

  async toggleFullscreen(): Promise<void> {
    const doc = document as Document & {
      webkitFullscreenElement?: Element;
      webkitExitFullscreen?: () => Promise<void>;
    };
    const container = (this.playerWrapper || this.container) as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void>;
    };

    const isRealFullscreen = !!(document.fullscreenElement || doc.webkitFullscreenElement);
    const isAnyFullscreen = isRealFullscreen || this.isFakeFullscreen;

    if (!isAnyFullscreen) {
      // Try to enter fullscreen
      let realFullscreenSucceeded = false;

      // Try real fullscreen first (works on desktop and iPad)
      try {
        if (container.requestFullscreen) {
          await container.requestFullscreen();
          realFullscreenSucceeded = true;
        } else if (container.webkitRequestFullscreen) {
          await container.webkitRequestFullscreen();
          realFullscreenSucceeded = true;
        }
      } catch (e) {
        if (this.debug) {
          console.log('[Fullscreen] Real fullscreen failed, falling back to CSS:', e);
        }
      }

      // If real fullscreen didn't work (iPhone), use CSS-based fake fullscreen
      if (!realFullscreenSucceeded) {
        this.enterFakeFullscreen();
      }
    } else {
      // Exit fullscreen
      if (isRealFullscreen) {
        // Exit real fullscreen
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (doc.webkitExitFullscreen) {
          await doc.webkitExitFullscreen();
        }
      } else if (this.isFakeFullscreen) {
        // Exit fake fullscreen
        this.exitFakeFullscreen();
      }
    }
  }

  /**
   * Enter CSS-based fake fullscreen (for iPhone where real fullscreen isn't available)
   */
  private enterFakeFullscreen(): void {
    const wrapper = this.playerWrapper || this.container.parentElement;
    if (!wrapper) return;

    // Store original styles so we can restore them
    this.originalStyles = {
      position: wrapper.style.position,
      top: wrapper.style.top,
      left: wrapper.style.left,
      right: wrapper.style.right,
      bottom: wrapper.style.bottom,
      width: wrapper.style.width,
      height: wrapper.style.height,
      zIndex: wrapper.style.zIndex,
      backgroundColor: wrapper.style.backgroundColor,
    };

    // Apply fullscreen styles
    wrapper.style.position = 'fixed';
    wrapper.style.top = '0';
    wrapper.style.left = '0';
    wrapper.style.right = '0';
    wrapper.style.bottom = '0';
    wrapper.style.width = '100vw';
    wrapper.style.height = '100vh';
    wrapper.style.zIndex = '999999';
    wrapper.style.backgroundColor = '#000';

    this.isFakeFullscreen = true;

    // Trigger resize to update renderer
    this.handleResize();

    // Notify callback
    this.callbacks.onFullscreenChange?.(true);

    if (this.debug) {
      console.log('[Fullscreen] Entered fake fullscreen (CSS-based)');
    }
  }

  /**
   * Exit CSS-based fake fullscreen
   */
  exitFakeFullscreen(): void {
    if (!this.isFakeFullscreen) return;

    const wrapper = this.playerWrapper || this.container.parentElement;
    if (!wrapper) return;

    // Restore original styles
    wrapper.style.position = this.originalStyles.position || '';
    wrapper.style.top = this.originalStyles.top || '';
    wrapper.style.left = this.originalStyles.left || '';
    wrapper.style.right = this.originalStyles.right || '';
    wrapper.style.bottom = this.originalStyles.bottom || '';
    wrapper.style.width = this.originalStyles.width || '';
    wrapper.style.height = this.originalStyles.height || '';
    wrapper.style.zIndex = this.originalStyles.zIndex || '';
    wrapper.style.backgroundColor = this.originalStyles.backgroundColor || '';

    this.isFakeFullscreen = false;

    // Trigger resize to update renderer
    this.handleResize();

    // Notify callback
    this.callbacks.onFullscreenChange?.(false);

    if (this.debug) {
      console.log('[Fullscreen] Exited fake fullscreen');
    }
  }

  async toggleGyro(): Promise<void> {
    if (this.isGyroActive) {
      this.disableGyro();
    } else {
      await this.enableGyro();
    }
  }

  hasGyroscopeAvailable(): boolean {
    return this.hasGyroscopeDevice;
  }

  isGyroEnabled(): boolean {
    return this.isGyroActive;
  }

  /**
   * Check if this device requires explicit permission request for motion sensors
   * (iOS 13+ requires user gesture to request DeviceOrientationEvent permission)
   * Also returns true if ?mockios=true is in the URL for testing purposes
   */
  static needsPermissionRequest(): boolean {
    // Check for mock iOS mode (for testing UI on non-iOS devices)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('mockios') === 'true') {
      return true;
    }

    return typeof DeviceOrientationEvent !== 'undefined' &&
           'requestPermission' in DeviceOrientationEvent;
  }

  /**
   * Request motion sensor permission (must be called from user gesture on iOS)
   * @returns 'granted' if permission was granted, 'denied' if denied, 'not-needed' if no permission required
   */
  async requestMotionPermission(): Promise<'granted' | 'denied' | 'not-needed'> {
    // Check for mock iOS mode (for testing UI on non-iOS devices)
    const urlParams = new URLSearchParams(window.location.search);
    const isMockMode = urlParams.get('mockios') === 'true';

    if (!VideoPlayer360Controller.needsPermissionRequest()) {
      // Non-iOS device, no permission needed - auto-enable gyro
      if (this.hasGyroscopeDevice && !this.isGyroActive) {
        await this.enableGyro();
      }
      return 'not-needed';
    }

    // In mock mode, simulate granting permission and enable gyro
    if (isMockMode) {
      if (this.debug) {
        console.log('[iOS Permission] Mock mode - simulating granted permission');
      }
      if (this.hasGyroscopeDevice && !this.isGyroActive) {
        await this.enableGyro();
      }
      return 'granted';
    }

    try {
      const permission = await (DeviceOrientationEvent as any).requestPermission();
      if (permission === 'granted') {
        // Permission granted - enable gyroscope
        await this.enableGyro();
        return 'granted';
      }
      if (this.debug) {
        console.log('[iOS Permission] Denied by user');
      }
      return 'denied';
    } catch (e) {
      if (this.debug) {
        console.error('[iOS Permission] Error:', e);
      }
      return 'denied';
    }
  }

  setViewPreset(preset: PresetName, animated = true, pane: PaneIndex = 0): void {
    const rig = this.rigFor(pane);
    if (rig) this.setRigPreset(rig, preset, animated);
  }

  private setRigPreset(rig: CameraRig, preset: PresetName, animated: boolean): void {
    if (this.debug) {
      const currentEuler = new THREE.Euler().setFromQuaternion(rig.intendedOrientation, 'YXZ');
      console.log('[setViewPreset] Called', {
        from: rig.preset,
        to: preset,
        animated,
        currentIntendedYaw: THREE.MathUtils.radToDeg(currentEuler.y).toFixed(1) + '°',
        currentIntendedPitch: THREE.MathUtils.radToDeg(currentEuler.x).toFixed(1) + '°',
      });
    }

    const view = this.viewPresets[preset];

    // Track current preset
    rig.preset = preset;
    rig.lastTransitionTime = Date.now();

    // Notify UI of preset change
    if (rig === this.main) this.callbacks.onViewPresetChange(preset);
    else this.callbacks.onSplitViewPresetChange?.(preset);

    // Extract current yaw from intended orientation to preserve compass heading
    const currentEuler = new THREE.Euler().setFromQuaternion(rig.intendedOrientation, 'YXZ');
    const currentYaw = currentEuler.y;

    // Apply view preset, preserving current yaw
    const alignedView = this.withAlignedYaw(view, currentYaw);
    this.applyViewPreset(rig, alignedView, animated);

    // Keep zoom enabled for all views
    rig.controls.mouseButtons.wheel = CameraControls.ACTION.ZOOM;
    rig.controls.touches.two = CameraControls.ACTION.TOUCH_ZOOM_TRUCK;

    // Recalibrate gyroscope reference when switching view modes
    // This prevents camera jumps when different modulation functions are used
    if (rig === this.main && this.isGyroActive && this.deviceQuat) {
      this.gyroReferenceQuat = this.deviceQuat.clone();

      // Reset smoothed offset to prevent jumps
      this.smoothedOffsetQuat.identity();

      // Recalibrate raw reference values
      this.rawAlphaReference = this.rawAlpha;
      this.rawBetaReference = this.rawBeta;
      this.rawGammaReference = this.rawGamma;

      if (this.debug) {
        console.log('[Gyro] Recalibrated for view preset change', {
          newPreset: preset,
          referenceAlpha: this.rawAlphaReference.toFixed(1) + '°',
          referenceBeta: this.rawBetaReference.toFixed(1) + '°',
          referenceGamma: this.rawGammaReference.toFixed(1) + '°',
        });
      }
    }
  }

  private withAlignedYaw(view: ViewPreset, azimuth: number): ViewPreset {
    const radius = Math.hypot(view.x, view.z);

    if (radius === 0) {
      return { ...view };
    }

    const x = radius * Math.sin(azimuth);
    const z = radius * Math.cos(azimuth);

    return {
      ...view,
      x,
      z,
    };
  }

  // Rotation adjustment: shift video texture on sphere
  // Split screen. Every extra camera starts as a copy of the main one — same
  // zoom, view preset and limits. In two free panes the right one is turned to
  // face the opposite way and is then steered on its own. In a locked layout
  // (all views) every pane, the main camera included, is reset to the normal
  // view and locked to face `headings[i]` (see lockedHeadings). Gyro and
  // spatial audio belong to the main camera alone, and gyro is off while locked.
  setLayout(layout: PaneLayout, headings: number[] = []): void {
    const { count, locked } = layout;
    if (count === this.layout.count && locked === this.layout.locked) {
      if (locked) this.aimLocked(headings);
      return;
    }

    for (const rig of this.extraRigs) rig.controls.dispose();
    this.splitPane?.remove();
    this.extraRigs = [];
    this.splitPane = null;
    this.layout = { count, locked };
    this.controls.enabled = !locked;

    if (count === 2 && !locked) {
      const pane = document.createElement('div');
      pane.style.cssText =
        'position:absolute;top:0;right:0;width:50%;height:100%;';
      this.container.appendChild(pane);
      const rig = this.cloneMainRig(pane);
      // Face the opposite way, so the two halves open on two different people
      // and not on the same picture twice.
      rig.controls.rotate(Math.PI, 0, false);
      this.splitPane = pane;
      this.extraRigs = [rig];
      this.callbacks.onSplitViewPresetChange?.(this.main.preset);
    } else if (locked) {
      for (let i = 1; i < count; i++) this.extraRigs.push(this.cloneMainRig());
      this.aimLocked(headings);
    }

    if (count === 1) this.renderer.setScissorTest(false);
    this.handleResize();
  }

  // The locked panes' framing. Takes effect at once when they are showing,
  // otherwise the next time they are.
  setFourView(framing: FourViewFraming): void {
    this.fourView = framing;
    if (this.layout.locked) {
      this.aimLocked(this.lockedHeadings);
      this.handleResize();
    }
  }

  // Locked panes: the normal view, each turned to its heading and zoomed in by
  // the framing's factor, so the one person each faces fills it. Re-run when
  // the participants (and so the headings) change.
  private aimLocked(headings: number[]): void {
    this.lockedHeadings = headings;
    const rigs = [this.main, ...this.extraRigs];
    const zoom = this.viewPresets.default.zoomFactor * this.fourView.zoom;
    rigs.forEach((rig, i) => {
      this.setRigPreset(rig, 'default', false);
      rig.controls.zoomTo(zoom, false);
      rig.isProgrammaticRotation = true;
      rig.controls.rotateAzimuthTo(this.shortestAzimuthTo(rig, headings[i] ?? 0), false);
      setTimeout(() => { rig.isProgrammaticRotation = false; }, 0);
    });
  }

  // A camera like the main one. With an element it takes pointer input there;
  // without one it takes none.
  private cloneMainRig(element?: HTMLElement): CameraRig {
    const camera = this.camera.clone();
    const controls = new CameraControls(camera, element);
    const from = this.controls;
    controls.azimuthRotateSpeed = from.azimuthRotateSpeed;
    controls.polarRotateSpeed = from.polarRotateSpeed;
    controls.minDistance = from.minDistance;
    controls.maxDistance = from.maxDistance;
    controls.minZoom = from.minZoom;
    controls.maxZoom = from.maxZoom;
    controls.minPolarAngle = from.minPolarAngle;
    controls.maxPolarAngle = from.maxPolarAngle;
    controls.dampingFactor = from.dampingFactor;
    controls.draggingDampingFactor = from.draggingDampingFactor;
    controls.dollySpeed = from.dollySpeed;
    controls.mouseButtons.wheel = from.mouseButtons.wheel;
    controls.touches.two = from.touches.two;
    controls.smoothTime = from.smoothTime;
    controls.draggingSmoothTime = from.draggingSmoothTime;

    const position = from.getPosition(new THREE.Vector3());
    const target = from.getTarget(new THREE.Vector3());
    controls.setLookAt(position.x, position.y, position.z, target.x, target.y, target.z, false);
    controls.zoomTo(this.camera.zoom, false);

    return {
      camera,
      controls,
      preset: this.main.preset,
      lastTransitionTime: Date.now(),
      isTransitioning: false,
      isProgrammaticRotation: false,
      intendedOrientation: this.main.intendedOrientation.clone(),
    };
  }

  setRotationAdjustment(degrees: number): void {
    this.rotationAdjustment = degrees;
    this.mesh.rotation.y = -Math.PI / 2 + THREE.MathUtils.degToRad(degrees);
  }

  setOnContextLost(cb: () => void): void {
    this.onContextLost = cb;
  }

  // Renders the current scene into a tiny offscreen target and returns its
  // pixels — a whole-frame downsample of what the viewer actually sees. Used
  // by the black-texture probe: sampling the WebGL output tests the pipeline
  // that can actually fail, unlike reading the <video> element through a 2D
  // canvas (whose readback can go black on some hardware-decode paths even
  // while the sphere renders fine). Returns null when the read fails
  // (e.g. context lost) so callers treat the result as inconclusive.
  getZoomDiagnostics(): ZoomDiagnostics {
    const fov = this.effectiveFov();
    const canvas = this.renderer.domElement;
    return {
      preset: this.currentPreset,
      zoom: this.camera.zoom,
      minZoom: this.controls.minZoom,
      maxZoom: this.controls.maxZoom,
      widest: this.widest,
      fovAcross: fov.horizontal,
      fovUpDown: fov.vertical,
      lensFov: this.camera.fov,
      aspect: this.camera.aspect,
      containerW: this.container.clientWidth,
      containerH: this.container.clientHeight,
      canvasW: canvas.width,
      canvasH: canvas.height,
      pixelRatio: this.renderer.getPixelRatio(),
      devicePixelRatio: window.devicePixelRatio,
      phoneTuning: this.hasGyroscopeDevice,
      wheelEvents: this.wheelEvents,
      lastWheel: this.lastWheel,
      gestureEvents: this.gestureEvents,
      requestPermissionApi: typeof DeviceOrientationEvent !== 'undefined' && 'requestPermission' in DeviceOrientationEvent,
      maxTouchPoints: navigator.maxTouchPoints,
    };
  }

  sampleFramePixels(size = 16): Uint8ClampedArray | null {
    const target = new THREE.WebGLRenderTarget(size, size);
    try {
      const buf = new Uint8Array(size * size * 4);
      this.renderer.setRenderTarget(target);
      this.renderer.render(this.scene, this.camera);
      this.renderer.readRenderTargetPixels(target, 0, 0, size, size, buf);
      return new Uint8ClampedArray(buf.buffer);
    } catch {
      return null;
    } finally {
      this.renderer.setRenderTarget(null);
      target.dispose();
    }
  }

  // Tears down only what this controller created: the render loop, the WebGL
  // scene, and its input/resize listeners. The media pipeline (video element,
  // video.js player, spatial audio) belongs to PlaybackCore and survives — a
  // sphere unmount is a presentation change, not a playback change.
  dispose(): void {
    // Stop animation
    if (this.animationFrameId !== null) {
      this.renderer.setAnimationLoop(null);
      this.animationFrameId = null;
    }

    // Cleanup event listeners
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('orientationchange', this.handleOrientationChange);
    this.container.removeEventListener('wheel', this.armWheelZoom, { capture: true });
    this.container.removeEventListener('pointerdown', this.armPinchZoom, { capture: true });
    this.container.removeEventListener('gesturechange', this.countGesture, { capture: true });
    this.containerResizeObserver?.disconnect();
    this.containerResizeObserver = null;

    // Cleanup gyroscope
    this.disableGyro();

    // Cleanup Three.js
    for (const rig of this.extraRigs) rig.controls.dispose();
    this.splitPane?.remove();
    this.controls.dispose();
    if (this.renderer.domElement && this.container.contains(this.renderer.domElement)) {
      this.container.removeChild(this.renderer.domElement);
    }
    this.renderer.dispose();
    
    if (this.videoEngine) {
      this.videoEngine.destroy();
    }
    
    this.mesh.geometry.dispose();
    if (Array.isArray(this.mesh.material)) {
      this.mesh.material.forEach((m) => m.dispose());
    } else {
      this.mesh.material.dispose();
    }
  }

  // ========================================================================
  // iOS Deferred Texture Creation Methods
  // ========================================================================

  // [DEPRECATED] Logic moved to VideoEngineFactory and Engines

  // Private methods

  private applyViewPreset(rig: CameraRig, view: ViewPreset, animated: boolean): void {
    // Mark that we're transitioning (to prevent zoom checks from interfering)
    rig.isTransitioning = true;

    if (this.debug) {
      console.log('[applyViewPreset] ===== Starting transition =====', {
        preset: rig.preset,
        animated,
        targetPosition: { x: view.x, y: view.y, z: view.z },
        polarLimits: { min: view.minPolarAngle + '°', max: view.maxPolarAngle + '°' },
      });
    }

    // Apply zoom limits from preset
    rig.controls.minZoom = view.minZoom;
    rig.controls.maxZoom = view.maxZoom;

    // Apply polar limits BEFORE transition (prevents rotation jitter)
    const DegToRad = THREE.MathUtils.degToRad;
    const oldMinPolar = THREE.MathUtils.radToDeg(rig.controls.minPolarAngle);
    const oldMaxPolar = THREE.MathUtils.radToDeg(rig.controls.maxPolarAngle);
    const currentPolar = THREE.MathUtils.radToDeg(rig.controls.polarAngle);

    rig.controls.minPolarAngle = DegToRad(view.minPolarAngle);
    rig.controls.maxPolarAngle = DegToRad(view.maxPolarAngle);

    if (this.debug) {
      console.log('[applyViewPreset] Polar limits updated BEFORE animation', {
        old: { min: oldMinPolar.toFixed(1) + '°', max: oldMaxPolar.toFixed(1) + '°' },
        new: { min: view.minPolarAngle + '°', max: view.maxPolarAngle + '°' },
        currentPolarAngle: currentPolar.toFixed(1) + '°',
      });
    }

    // Use CameraControls animation
    Promise.all([
      rig.controls.setLookAt(
        view.x,
        view.y,
        view.z,
        view.targetX,
        view.targetY,
        view.targetZ,
        animated
      ),
      rig.controls.zoomTo(view.zoomFactor, animated),
    ])
      .then(() => {
        // Update intended orientation to match camera's final orientation
        rig.intendedOrientation.copy(rig.camera.quaternion);

        // Ensure controls remain enabled and functional — except the main
        // camera's in a locked layout, which takes no input.
        rig.controls.enabled = !(rig === this.main && this.layout.locked);
        rig.controls.mouseButtons.left = CameraControls.ACTION.ROTATE;
        rig.controls.touches.one = CameraControls.ACTION.TOUCH_ROTATE;

        const finalPolar = THREE.MathUtils.radToDeg(rig.controls.polarAngle);
        if (this.debug) {
          console.log('[applyViewPreset] ===== Transition complete =====', {
            preset: rig.preset,
            finalPolarAngle: finalPolar.toFixed(1) + '°',
            polarLimits: {
              min: THREE.MathUtils.radToDeg(rig.controls.minPolarAngle).toFixed(1) + '°',
              max: THREE.MathUtils.radToDeg(rig.controls.maxPolarAngle).toFixed(1) + '°',
            },
            finalPosition: {
              x: rig.camera.position.x.toFixed(2),
              y: rig.camera.position.y.toFixed(2),
              z: rig.camera.position.z.toFixed(2),
            },
          });
        }

        // Enable detailed polar angle logging for the next 3 seconds
        if (this.debug) {
          this.logPolarChangesUntil = Date.now() + 3000;
          this.lastLoggedPolarAngle = finalPolar;
          console.log('[applyViewPreset] Enabled detailed polar logging for 3 seconds');
        }

        // Transition complete
        rig.isTransitioning = false;
      })
      .catch((err) => {
        if (this.debug) {
          console.error('Error during view preset transition:', err);
        }
        rig.controls.enabled = !(rig === this.main && this.layout.locked);
        rig.isTransitioning = false;
      });
  }

  // Zooming far enough out of the normal view opens the above view, and
  // zooming back in returns from either overhead view. Each camera does this
  // on its own zoom.
  private applyZoomTransitions(rig: CameraRig, now: number): void {
    const currentZoom = rig.camera.zoom;
    const timeSinceLastTransition = now - rig.lastTransitionTime;

    // Only check for transitions if not currently transitioning and enough time has passed
    if (!rig.isTransitioning && timeSinceLastTransition > 50) {
      // Debug logging every 3 seconds
      if (this.debug && now - this.lastZoomTransitionLogTime >= 3000) {
        console.log('[Zoom Transition Check]', {
          currentPreset: rig.preset,
          currentZoom: currentZoom.toFixed(3),
          normalToTopThreshold: this.zoomConfig.normalToTopThreshold.toFixed(3),
          topToNormalThreshold: this.zoomConfig.topToNormalThreshold.toFixed(3),
          timeSinceLastTransition: timeSinceLastTransition + 'ms'
        });
        this.lastZoomTransitionLogTime = now;
      }

      if (rig.preset === 'default') {
        // Normal view: check if we should transition to top-down
        if (currentZoom <= this.zoomConfig.normalToTopThreshold) {
          if (this.debug) {
            console.log('[Zoom Transition] Normal → Top-Down (zoom:', currentZoom.toFixed(2), ')');
          }
          this.setRigPreset(rig, 'topDown', true);  // Smooth animated transition
        }
      } else if (rig.preset === 'topDown') {
        // Top-down view: check if we should transition back to normal
        if (currentZoom >= this.zoomConfig.topToNormalThreshold) {
          if (this.debug) {
            console.log('[Zoom Transition] Top-Down → Normal (zoom:', currentZoom.toFixed(2), ')');
          }
          this.setRigPreset(rig, 'default', true);  // Smooth animated transition
        }
      } else if (rig.preset === 'bottomUp') {
        // Bottom-up view: check if we should transition to normal (one-way transition)
        if (currentZoom >= this.zoomConfig.bottomUpToNormalThreshold) {
          if (this.debug) {
            console.log('[Zoom Transition] Bottom-Up → Normal (zoom:', currentZoom.toFixed(2), ')');
          }
          this.setRigPreset(rig, 'default', true);  // Smooth animated transition
        }
      }
      // Note: bottomUp → normal is one-way (zoom in only)
      //       normal → bottomUp is manual only (prevents unwanted transitions)
    }
  }

  // The other cameras' share of a frame: the same bookkeeping the main
  // camera gets in the loop below, less everything that is the main camera's
  // alone (gyro, spatial audio, diagnostics). Only the two-pane layout's
  // right camera is steerable, so only it reports and zooms between views.
  private updateExtraRigs(delta: number): void {
    for (const rig of this.extraRigs) {
      rig.controls.update(delta);
      rig.intendedOrientation.copy(rig.camera.quaternion);
    }
    if (this.layout.count !== 2 || this.layout.locked) return;
    const rig = this.extraRigs[0];
    const euler = new THREE.Euler().setFromQuaternion(rig.intendedOrientation, 'YXZ');
    const yawDegrees = ((THREE.MathUtils.radToDeg(euler.y) % 360) + 360) % 360;
    const fov = this.effectiveFov(rig.camera);
    this.callbacks.onSplitCameraUpdate?.(
      yawDegrees, THREE.MathUtils.radToDeg(euler.x), fov.horizontal, fov.vertical
    );
    this.applyZoomTransitions(rig, Date.now());
  }

  private startAnimation(): void {
    const sph = new THREE.Spherical();

    const animate = () => {
      const delta = this.clock.getDelta();

      // Update Video Engine (required for Canvas fallback)
      this.videoEngine.update();

      // Update controls - CameraControls manages camera orientation
      this.controls.update(delta);
      this.updateExtraRigs(delta);

      // Apply gyroscope offset if active (selective axis approach)
      if (this.isGyroActive && this.gyroReferenceQuat && !this.layout.locked) {
        // Motion as two numbers: how far the phone has turned (heading) and
        // tilted (elevation) since Motion was switched on, each read off the
        // phone's world orientation and its reference separately.
        //
        // Gavin, 3 Nov, on Android and iPhone: "we can pan but not tilt". The
        // offset used to be deviceQuat × referenceQuat⁻¹ — a rotation in the
        // WORLD frame — split into yaw / pitch / roll with roll thrown away.
        // A tilt is a turn about the phone's own sideways axis, so in the
        // world frame it lands in pitch only when the reference heading
        // happened to be 0°: at 90° or 270° it was all roll, and discarded;
        // at 180° it was inverted. The heading (alpha) at switch-on is
        // whatever the sensor says, so most of the time tilt vanished.
        // Differencing the two Euler readings has no such dependence.
        const device = new THREE.Euler().setFromQuaternion(this.deviceQuat, 'YXZ')
        const reference = new THREE.Euler().setFromQuaternion(this.gyroReferenceQuat, 'YXZ')
        const turned = Math.atan2(Math.sin(device.y - reference.y), Math.cos(device.y - reference.y))
        const tilted = device.x - reference.x
        this.gyroOffsetQuat.setFromEuler(new THREE.Euler(tilted, turned, 0, 'YXZ'))

        // Smoothing to reduce jitter (slerp = spherical linear interpolation)
        // filters sensor noise and gives smooth motion.
        this.smoothedOffsetQuat.slerp(this.gyroOffsetQuat, this.gyroSmoothingFactor)
        const offset = new THREE.Euler().setFromQuaternion(this.smoothedOffsetQuat, 'YXZ')

        // Added to where the drag / view preset has the camera pointing, with
        // no roll: the horizon stays level whatever the phone does.
        const baseline = new THREE.Euler().setFromQuaternion(this.camera.quaternion, 'YXZ')
        this.camera.quaternion.setFromEuler(new THREE.Euler(baseline.x + offset.x, baseline.y + offset.y, 0, 'YXZ'))

        // Respect polar angle limits from current preset
        const currentView = this.viewPresets[this.currentPreset];
        const minPolar = THREE.MathUtils.degToRad(currentView.minPolarAngle);
        const maxPolar = THREE.MathUtils.degToRad(currentView.maxPolarAngle);

        // Extract polar angle and clamp it
        const euler = new THREE.Euler().setFromQuaternion(this.camera.quaternion, 'YXZ');
        const polarAngle = euler.x + Math.PI / 2; // Convert pitch to polar angle

        if (polarAngle < minPolar || polarAngle > maxPolar) {
          const clampedPolar = THREE.MathUtils.clamp(polarAngle, minPolar, maxPolar);
          euler.x = clampedPolar - Math.PI / 2;
          this.camera.quaternion.setFromEuler(euler);
        }
      }

      // Sync intended orientation with camera's current orientation
      this.intendedOrientation.copy(this.camera.quaternion);

      // Update camera state for UI (compass)
      // Extract yaw from intended orientation for consistent compass across all modes
      const intendedEuler = new THREE.Euler().setFromQuaternion(this.intendedOrientation, 'YXZ');
      let yawDegrees = THREE.MathUtils.radToDeg(intendedEuler.y);

      // Normalize to 0-360 range for compass display
      yawDegrees = ((yawDegrees % 360) + 360) % 360;

      // Pitch is the view elevation: 0 at the horizon, negative looking down.
      // It is the polar angle less 90°, the same relation the gyro clamp above
      // uses, and is what setCamera() takes back.
      const pitchDegrees = THREE.MathUtils.radToDeg(intendedEuler.x);

      const fov = this.effectiveFov();
      this.callbacks.onCameraUpdate(yawDegrees, pitchDegrees, fov.horizontal, fov.vertical);

      // Update spatial audio listener orientation to match camera
      this.spatialAudio.updateListenerOrientation(yawDegrees);

      // Get current zoom and time for both transitions and logging
      const currentZoom = this.camera.zoom;
      if (!this.main.isTransitioning && (!this.widest || currentZoom < this.widest.zoom)) {
        this.widest = { zoom: currentZoom, preset: this.currentPreset, fovAcross: fov.horizontal };
      }
      const now = Date.now();

      // Detailed polar angle logging after transitions (for 3 seconds)
      if (this.debug && now < this.logPolarChangesUntil) {
        const currentPolar = THREE.MathUtils.radToDeg(this.controls.polarAngle);
        if (Math.abs(currentPolar - this.lastLoggedPolarAngle) > 0.5) {
          console.log('[Polar Angle Change]', {
            polarAngle: currentPolar.toFixed(2) + '°',
            change: (currentPolar - this.lastLoggedPolarAngle).toFixed(2) + '°',
            limits: {
              min: THREE.MathUtils.radToDeg(this.controls.minPolarAngle).toFixed(1) + '°',
              max: THREE.MathUtils.radToDeg(this.controls.maxPolarAngle).toFixed(1) + '°',
            },
            preset: this.currentPreset,
          });
          this.lastLoggedPolarAngle = currentPolar;
        }
      }

      // Automatic zoom-based view transitions (only normal ↔ top-down). Not
      // while locked: nobody is zooming, and the panes stay in the normal view.
      if (!this.layout.locked) this.applyZoomTransitions(this.main, now);

      // Debounced camera state logging (once per second if changed)
      if (this.debug && now - this.lastLogTime >= 1000) {
        const currentPosition = this.camera.position;
        const zoomChanged = Math.abs(currentZoom - this.lastLoggedZoom) > 0.01;
        const yawChanged = Math.abs(yawDegrees - this.lastLoggedYaw) > 1;
        const positionChanged =
          Math.abs(currentPosition.x - this.lastLoggedPosition.x) > 0.01 ||
          Math.abs(currentPosition.y - this.lastLoggedPosition.y) > 0.01 ||
          Math.abs(currentPosition.z - this.lastLoggedPosition.z) > 0.01;

        if (zoomChanged || yawChanged || positionChanged) {
          console.log('[Camera State]', {
            zoom: currentZoom.toFixed(3),
            yaw: yawDegrees.toFixed(1) + '°',
            position: {
              x: currentPosition.x.toFixed(2),
              y: currentPosition.y.toFixed(2),
              z: currentPosition.z.toFixed(2),
            },
          });

          this.lastLogTime = now;
          this.lastLoggedZoom = currentZoom;
          this.lastLoggedYaw = yawDegrees;
          this.lastLoggedPosition = {
            x: currentPosition.x,
            y: currentPosition.y,
            z: currentPosition.z
          };
        }
      }

      // Render
      if (this.layout.count > 1) {
        const { x: width, y: height } = this.renderer.getSize(this.rendererSize);
        // Three panes leave the corners either side of the bottom one empty,
        // and locked panes leave bars round the centred block: clear the whole canvas
        // so nothing stale shows there.
        this.renderer.setScissorTest(false);
        this.renderer.clear();
        this.renderer.setScissorTest(true);
        const cameras = [this.camera, ...this.extraRigs.map((rig) => rig.camera)];
        paneRects(this.layout, width, height, this.fourView.paneAspect).forEach((r, i) => {
          // WebGL counts y up from the bottom; the rects count down from the top.
          const y = height - r.y - r.height;
          this.renderer.setViewport(r.x, y, r.width, r.height);
          this.renderer.setScissor(r.x, y, r.width, r.height);
          this.renderer.render(this.scene, cameras[i]);
        });
      } else {
        this.renderer.render(this.scene, this.camera);
      }
    };

    this.renderer.setAnimationLoop(animate);
  }


  /**
   * How much of the scene is on screen right now, in degrees: across (left
   * edge to right edge) and up-and-down. This is what the radar draws.
   *
   * `camera.fov` is NOT it: it is set once at construction and never moves.
   * Zooming here scales `camera.zoom`, which THREE divides the frustum by, so
   * the angle a viewer can see is the lens angle opened out by the zoom.
   * `camera.fov` is the up-and-down angle; the aspect ratio gives the across.
   *
   * Normal view: zoom runs 0.45 (where it switches to Above) to 3.5, which
   * on a 4:3 player is roughly 132 degrees across down to 33.
   */
  private effectiveFov(camera = this.camera): { horizontal: number; vertical: number } {
    const halfVertical = Math.atan(
      Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2) / camera.zoom
    );
    const halfHorizontal = Math.atan(Math.tan(halfVertical) * camera.aspect);
    return {
      horizontal: THREE.MathUtils.radToDeg(halfHorizontal) * 2,
      vertical: THREE.MathUtils.radToDeg(halfVertical) * 2,
    };
  }

  private handleResize = (): void => {
    if (!this.container) return;

    const width = this.container.clientWidth;
    const height = this.container.clientHeight;

    // Every pane of a layout has the same shape: half the width in two free
    // panes, the framing's shape when locked.
    const pane = paneRects(this.layout, width, height, this.fourView.paneAspect)[0];
    const aspect = pane.width / pane.height;
    for (const camera of [this.camera, ...this.extraRigs.map((rig) => rig.camera)]) {
      camera.aspect = aspect;
      camera.updateProjectionMatrix();
    }
    this.renderer.setSize(width, height);
  };

  /**
   * Handle screen orientation changes (portrait ↔ landscape)
   * Updates orientation compensation and recalibrates gyroscope if active
   */
  private handleOrientationChange = (): void => {
    const newOrientation = window.orientation || 0;

    if (this.debug) {
      console.log('[Orientation Change]', {
        old: this.screenOrientation,
        new: newOrientation,
        gyroActive: this.isGyroActive,
      });
    }

    this.screenOrientation = newOrientation;

    // If gyroscope is active, recalibrate reference to maintain stable view
    if (this.isGyroActive && this.deviceQuat) {
      this.gyroReferenceQuat = this.deviceQuat.clone();

      // Reset smoothed offset to prevent jumps
      this.smoothedOffsetQuat.identity();

      // Recalibrate raw reference values
      this.rawAlphaReference = this.rawAlpha;
      this.rawBetaReference = this.rawBeta;
      this.rawGammaReference = this.rawGamma;

      if (this.debug) {
        console.log('[Gyro] Recalibrated for orientation change', {
          referenceAlpha: this.rawAlphaReference.toFixed(1) + '°',
          referenceBeta: this.rawBetaReference.toFixed(1) + '°',
          referenceGamma: this.rawGammaReference.toFixed(1) + '°',
        });
      }
    }
  };

  /**
   * Enable gyroscope/device orientation tracking
   * Requests permission on iOS and sets up event listeners
   */
  async enableGyro(): Promise<void> {
    if (this.isGyroActive) return;

    try {
      // Request permission on iOS 13+
      if (typeof DeviceOrientationEvent !== 'undefined' && 'requestPermission' in DeviceOrientationEvent) {
        const permission = await (DeviceOrientationEvent as any).requestPermission();
        if (permission !== 'granted') {
          if (this.debug) console.log('[Gyro] Permission denied');
          return;
        }
      }

      // Capture current screen orientation
      this.screenOrientation = window.orientation || 0;

      // Create bound handler
      this.deviceOrientationHandler = this.handleDeviceOrientation.bind(this);

      // Add event listener
      window.addEventListener('deviceorientation', this.deviceOrientationHandler);

      // Capture reference orientation (current device position becomes neutral)
      // Wait a bit for first reading
      await new Promise(resolve => setTimeout(resolve, 100));

      if (this.deviceQuat) {
        this.gyroReferenceQuat = this.deviceQuat.clone();

        // Reset smoothed offset to identity (no accumulated smoothing)
        this.smoothedOffsetQuat.identity();

        // Capture raw reference values for modulation functions
        this.rawAlphaReference = this.rawAlpha;
        this.rawBetaReference = this.rawBeta;
        this.rawGammaReference = this.rawGamma;

        this.isGyroActive = true;

        // Notify UI of gyro state change
        this.callbacks.onGyroStateChange(true);

        if (this.debug) {
          console.log('[Gyro] Enabled', {
            referenceQuat: this.gyroReferenceQuat,
            referenceAlpha: this.rawAlphaReference.toFixed(1) + '°',
            referenceBeta: this.rawBetaReference.toFixed(1) + '°',
            referenceGamma: this.rawGammaReference.toFixed(1) + '°',
            screenOrientation: this.screenOrientation,
          });
        }
      }
    } catch (error) {
      if (this.debug) console.error('[Gyro] Failed to enable:', error);
    }
  }

  /**
   * Disable gyroscope tracking
   */
  private disableGyro(): void {
    if (!this.isGyroActive) return;

    if (this.deviceOrientationHandler) {
      window.removeEventListener('deviceorientation', this.deviceOrientationHandler);
      this.deviceOrientationHandler = null;
    }

    this.isGyroActive = false;
    this.gyroReferenceQuat = null;

    // Notify UI of gyro state change
    this.callbacks.onGyroStateChange(false);

    if (this.debug) console.log('[Gyro] Disabled');
  }

  /**
   * Handle device orientation events
   * Stores raw values and converts alpha/beta/gamma to Three.js quaternion
   */
  private handleDeviceOrientation(event: DeviceOrientationEvent): void {
    if (event.alpha === null || event.beta === null || event.gamma === null) {
      return;
    }

    // Store raw device orientation values for modulation functions
    this.rawAlpha = event.alpha;
    this.rawBeta = event.beta;
    this.rawGamma = event.gamma;

    // Use industry-standard DeviceOrientationControls conversion
    this.setObjectQuaternion(
      this.deviceQuat,
      event.alpha,
      event.beta,
      event.gamma,
      this.screenOrientation
    );

    // Also update legacy quaternion for backward compatibility during transition
    this.currentDeviceQuat = this.deviceQuat.clone();
  }

  /**
   * Convert device orientation (alpha, beta, gamma) to Three.js quaternion
   * Uses W3C Device Orientation specification reference implementation
   *
   * Device Orientation API (ZXY intrinsic rotation order):
   * - alpha: compass heading (0-360°), rotation around Z-axis
   * - beta: front-back tilt (-180 to 180°), rotation around X-axis
   * - gamma: left-right tilt (-90 to 90°), rotation around Y-axis
   *
   * This implementation avoids gimbal lock by composing all three angles
   * into a quaternion using the W3C reference formula.
   *
   * @see https://www.w3.org/TR/orientation-event/
   */
  private getDeviceOrientationAsQuaternion(
    alpha: number,
    beta: number,
    gamma: number,
    screenOrientation: number
  ): THREE.Quaternion {
    // Convert degrees to radians
    // Using W3C convention: ZXY intrinsic rotation order
    const _z = alpha ? THREE.MathUtils.degToRad(alpha) : 0;  // Z: compass (alpha)
    const _x = beta ? THREE.MathUtils.degToRad(beta) : 0;    // X: front-back tilt (beta)
    const _y = gamma ? THREE.MathUtils.degToRad(gamma) : 0;  // Y: left-right tilt (gamma)

    // W3C quaternion formula for ZXY rotation composition
    // This avoids gimbal lock by computing quaternion directly from all three angles
    const cX = Math.cos(_x / 2);
    const cY = Math.cos(_y / 2);
    const cZ = Math.cos(_z / 2);
    const sX = Math.sin(_x / 2);
    const sY = Math.sin(_y / 2);
    const sZ = Math.sin(_z / 2);

    const w = cX * cY * cZ - sX * sY * sZ;
    const x = sX * cY * cZ - cX * sY * sZ;
    const y = cX * sY * cZ + sX * cY * sZ;
    const z = cX * cY * sZ + sX * sY * cZ;

    // Create Three.js quaternion (Three.js uses x, y, z, w order)
    const quat = new THREE.Quaternion(x, y, z, w);

    // Apply screen orientation compensation if needed
    if (screenOrientation !== 0) {
      const screenQuat = new THREE.Quaternion().setFromAxisAngle(
        new THREE.Vector3(0, 0, 1),
        THREE.MathUtils.degToRad(screenOrientation)
      );
      quat.multiply(screenQuat);
    }

    if (this.debug) {
      console.log('[Gyro Debug]', {
        alpha: alpha.toFixed(1) + '°',
        beta: beta.toFixed(1) + '°',
        gamma: gamma.toFixed(1) + '°',
        quat: `(${quat.x.toFixed(3)}, ${quat.y.toFixed(3)}, ${quat.z.toFixed(3)}, ${quat.w.toFixed(3)})`,
      });
    }

    return quat;
  }

  /**
   * Convert device orientation to camera quaternion using industry-standard algorithm
   * Based on three.js DeviceOrientationControls
   *
   * This method properly combines all three device orientation axes (alpha, beta, gamma)
   * with the necessary coordinate transformations for camera viewing.
   *
   * @param quaternion - Output quaternion to modify
   * @param alpha - Compass heading (0-360°)
   * @param beta - Front-back tilt (-180 to 180°)
   * @param gamma - Left-right tilt (-90 to 90°)
   * @param screenOrientation - Screen orientation in degrees (0, 90, -90, 180)
   */
  private setObjectQuaternion(
    quaternion: THREE.Quaternion,
    alpha: number,
    beta: number,
    gamma: number,
    screenOrientation: number
  ): void {
    // Convert degrees to radians
    const alphaRad = THREE.MathUtils.degToRad(alpha);
    const betaRad = THREE.MathUtils.degToRad(beta);
    const gammaRad = THREE.MathUtils.degToRad(gamma);
    const orientRad = THREE.MathUtils.degToRad(screenOrientation);

    // Step 1: Set Euler angles (YXZ order matches device orientation API)
    const euler = new THREE.Euler(betaRad, alphaRad, -gammaRad, 'YXZ');

    // Step 2: Convert to quaternion (avoids gimbal lock)
    quaternion.setFromEuler(euler);

    // Step 3: Apply device-to-camera correction (-90° around X-axis)
    // This rotates the camera so it looks from the device back (not top)
    quaternion.multiply(this.q1);

    // Step 4: Apply screen orientation compensation
    // Rotates around Z-axis to account for portrait/landscape
    const q0 = new THREE.Quaternion().setFromAxisAngle(this.zee, -orientRad);
    quaternion.multiply(q0);
  }

  /**
   * Determine if current screen orientation is portrait or landscape
   * @returns 'portrait' for 0° and 180°, 'landscape' for 90° and -90°
   */
  private getScreenOrientationType(): 'portrait' | 'landscape' {
    const orientation = Math.abs(this.screenOrientation);
    return orientation === 90 ? 'landscape' : 'portrait';
  }

  // ========================================================================
  // Gyroscope Modulation Functions
  // These functions control how gyroscope/compass inputs affect the camera
  // for each combination of view mode and screen orientation
  // ========================================================================

  /**
   * Modulate gyroscope input for Normal view in Portrait orientation
   * Uses compass (alpha) to control camera yaw for body rotation tracking
   * @param alpha Compass heading (0-360°)
   * @param beta Front-back tilt (-180 to 180°)
   * @param gamma Left-right tilt (-90 to 90°)
   * @returns Rotation adjustments in degrees {x: pitch, y: yaw, z: roll}
   */
  private modulateGyroNormalPortrait(
    alpha: number,
    beta: number,
    gamma: number
  ): { x: number; y: number; z: number } {
    // Calculate compass delta from reference (handles 360° wraparound)
    // Formula ensures shortest path, resulting in range [-180, 180]
    let deltaAlpha = alpha - this.rawAlphaReference;
    deltaAlpha = ((deltaAlpha + 180) % 360) - 180;
    if (deltaAlpha < -180) deltaAlpha += 360;

    // Return yaw rotation (negate for correct direction)
    // When you turn right (alpha increases), camera should turn right with you
    return {
      x: 0,           // No pitch adjustment
      y: alpha, // Yaw follows compass (negated for correct direction)
      z: 0            // No roll adjustment
    };
  }

  /**
   * Modulate gyroscope input for Normal view in Landscape orientation
   * @param alpha Compass heading (0-360°)
   * @param beta Front-back tilt (-180 to 180°)
   * @param gamma Left-right tilt (-90 to 90°)
   * @returns Rotation adjustments in degrees {x: pitch, y: yaw, z: roll}
   */
  private modulateGyroNormalLandscape(
    alpha: number,
    beta: number,
    gamma: number
  ): { x: number; y: number; z: number } {
    // Baseline: no gyroscope effect
    return { x: 0, y: 0, z: 0 };
  }

  /**
   * Modulate gyroscope input for Above view in Portrait orientation
   * @param alpha Compass heading (0-360°)
   * @param beta Front-back tilt (-180 to 180°)
   * @param gamma Left-right tilt (-90 to 90°)
   * @returns Rotation adjustments in degrees {x: pitch, y: yaw, z: roll}
   */
  private modulateGyroAbovePortrait(
    alpha: number,
    beta: number,
    gamma: number
  ): { x: number; y: number; z: number } {
    // Baseline: no gyroscope effect
    return { x: 0, y: 0, z: 0 };
  }

  /**
   * Modulate gyroscope input for Above view in Landscape orientation
   * @param alpha Compass heading (0-360°)
   * @param beta Front-back tilt (-180 to 180°)
   * @param gamma Left-right tilt (-90 to 90°)
   * @returns Rotation adjustments in degrees {x: pitch, y: yaw, z: roll}
   */
  private modulateGyroAboveLandscape(
    alpha: number,
    beta: number,
    gamma: number
  ): { x: number; y: number; z: number } {
    // Baseline: no gyroscope effect
    return { x: 0, y: 0, z: 0 };
  }

  /**
   * Modulate gyroscope input for Below view in Portrait orientation
   * @param alpha Compass heading (0-360°)
   * @param beta Front-back tilt (-180 to 180°)
   * @param gamma Left-right tilt (-90 to 90°)
   * @returns Rotation adjustments in degrees {x: pitch, y: yaw, z: roll}
   */
  private modulateGyroBelowPortrait(
    alpha: number,
    beta: number,
    gamma: number
  ): { x: number; y: number; z: number } {
    // Baseline: no gyroscope effect
    return { x: 0, y: 0, z: 0 };
  }

  /**
   * Modulate gyroscope input for Below view in Landscape orientation
   * @param alpha Compass heading (0-360°)
   * @param beta Front-back tilt (-180 to 180°)
   * @param gamma Left-right tilt (-90 to 90°)
   * @returns Rotation adjustments in degrees {x: pitch, y: yaw, z: roll}
   */
  private modulateGyroBelowLandscape(
    alpha: number,
    beta: number,
    gamma: number
  ): { x: number; y: number; z: number } {
    // Baseline: no gyroscope effect
    return { x: 0, y: 0, z: 0 };
  }

  /**
   * Route to appropriate gyroscope modulation function based on current
   * view mode and screen orientation
   * @param alpha Compass heading (0-360°)
   * @param beta Front-back tilt (-180 to 180°)
   * @param gamma Left-right tilt (-90 to 90°)
   * @returns Rotation adjustments in degrees {x: pitch, y: yaw, z: roll}
   */
  private getGyroModulation(
    alpha: number,
    beta: number,
    gamma: number
  ): { x: number; y: number; z: number } {
    const orientationType = this.getScreenOrientationType();

    // Route to appropriate modulation function based on view mode and orientation
    if (this.currentPreset === 'default') {
      // Normal view
      return orientationType === 'portrait'
        ? this.modulateGyroNormalPortrait(alpha, beta, gamma)
        : this.modulateGyroNormalLandscape(alpha, beta, gamma);
    } else if (this.currentPreset === 'topDown') {
      // Above view
      return orientationType === 'portrait'
        ? this.modulateGyroAbovePortrait(alpha, beta, gamma)
        : this.modulateGyroAboveLandscape(alpha, beta, gamma);
    } else {
      // Below view
      return orientationType === 'portrait'
        ? this.modulateGyroBelowPortrait(alpha, beta, gamma)
        : this.modulateGyroBelowLandscape(alpha, beta, gamma);
    }
  }

  /**
   * Detects if the current device is running iOS (iPhone, iPad, iPod)
   * Uses multiple detection methods for reliability
   */
  static isIOSDevice(): boolean {
    // Check for mock iOS mode (for testing on non-iOS devices)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('mockios') === 'true') {
      return true;
    }

    // Primary detection: Check userAgent
    const ua = navigator.userAgent;
    const isIOSUA = /iPad|iPhone|iPod/.test(ua);

    // iPad on iOS 13+ reports as Mac in userAgent, check for touch support
    const isIPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;

    return isIOSUA || isIPadOS;
  }

  /**
   * Detects if the current device is a mobile device
   * Checks for touch support and device characteristics
   */
  private static isMobileDevice(): boolean {
    // Check for touch support
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Check user agent for mobile devices
    const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    const isMobileUA = mobileRegex.test(navigator.userAgent);

    // Check for narrow viewport (typical of mobile devices)
    const isNarrowViewport = window.innerWidth <= 768;

    return hasTouch && (isMobileUA || isNarrowViewport);
  }

  /**
   * Detects if the current device has a gyroscope/orientation sensor
   * Used to determine device-specific zoom configurations
   */
  private static hasGyroscope(): boolean {
    // Check if DeviceOrientationEvent exists
    if (typeof DeviceOrientationEvent === 'undefined') {
      return false;
    }

    // iPhone and iPad. This used to be "DeviceOrientationEvent.requestPermission
    // exists", which was an iOS-only API when it was written. Chrome and Brave
    // on a Mac now expose it too (Gavin, 28 Sep: Chrome 153, Brave 152), so a
    // desktop Mac was given phone presets and zoom tuning — his ?zoominfo=1
    // screenshots read "tuning: phone". Ask about the device, not the API.
    if (VideoPlayer360Controller.isIOSDevice()) {
      return true;
    }

    // For other devices, assume gyroscope if orientation event exists
    // We can't test synchronously, so we use heuristics
    return this.isMobileDevice();
  }
}
